import _extends from '@babel/runtime/helpers/extends';
import React from 'react';
import PropTypes from 'prop-types';
import { p as props, d as defaults } from './componentEl-fcbc301e.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-1ed461aa.js';

/*
Generic component for Tooltip , to be used in the context of Tooltip Trigger
The hover event on this element will trigger the Tooltip Content to be displayed
*/

function Trigger({
  componentEl: Component,
  className,
  children,
  ...otherProps
}) {
  let cssClasses = ['st_tooltip_trigger'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
Trigger.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
Trigger.defaultProps = {
  componentEl: defaults,
  className: null
};

/*
Generic component for Tooltip Content, to be used in the context of Tooltip Content
Accepts `position` (which side of the Triggershould the content be place) and
`size` (dictates the size of the actual Content box, if any)
*/

function Content({
  componentEl: Component,
  className,
  children,
  position,
  size,
  ...otherProps
}) {
  let cssClasses = ['st_tooltip_content', `st_tooltip_content--${position}`, `st_tooltip_content--${size}`];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
Content.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** dictates the position of the tool tip content */
  position: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  /** dictates the size of the tool tip content if any  */
  size: PropTypes.oneOf(['none', 'md', 'mlg', 'lg', 'xl'])
};
Content.defaultProps = {
  componentEl: 'div',
  className: null,
  position: 'top',
  size: 'none'
};

/*
Generic component for Tooltip
Accepts one Trigger and One Content
Does not do any state management! See Storybook for examples for state management
*/

function Tooltip({
  componentEl: Component,
  type,
  className,
  children,
  delay,
  // eslint-disable-next-line react/prop-types
  style = {},
  ...otherProps
}) {
  let cssClasses = ['st_tooltip'];
  if (type && type !== 'default') cssClasses.push(`st_tooltip--${type}`);
  cssClasses = addClassNames(className, cssClasses);
  const styleProp = style || {};
  styleProp['--st-tooltip-delay'] = delay;
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' '),
    style: style
  }, otherProps), children);
}
Tooltip.propTypes = {
  /** behavior of tooltip, appears on hover by default */
  type: PropTypes.oneOf(['default', 'permanent']),
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** how long user should hover over trigger to display tooltip,
   * to be passed to CSS transition-delay property */
  delay: PropTypes.string
};
Tooltip.defaultProps = {
  componentEl: defaults,
  type: 'default',
  className: null,
  delay: '400ms'
};
Tooltip.Trigger = Trigger;
Tooltip.Trigger.displayName = 'Tooltip.Trigger';
Tooltip.Content = Content;
Tooltip.Content.displayName = 'Tooltip.Content';

export { Tooltip as T };
